class BotpressService {

	private static instance: BotpressService;
	private scriptLoaded = false;
	private loadingPromise: Promise<void> | null = null;

	private readonly INJECT_ID = 'bp-inject-script';
	private readonly CONFIG_ID = 'bp-config-script';
	private readonly INJECT_SRC = 'https://cdn.botpress.cloud/webchat/v3.6/inject.js';
	private readonly CONFIG_SRC = 'https://files.bpcontent.cloud/2026/07/15/14/20260715142444-2TCQSDCG.js';

	private constructor () {}

	static getInstance (): BotpressService {
		if (!BotpressService.instance) {
			BotpressService.instance = new BotpressService();
		}
		return BotpressService.instance;
	}

	/**
	 * Charge le chatbot Botpress de manière asynchrone et idempotente.
	 * Deux scripts sont nécessaires : inject.js (qui expose window.botpress),
	 * puis le script de configuration (qui initialise le webchat).
	 * Le second dépend du premier, on les enchaîne donc dans l'ordre.
	 */
	async loadAsync (): Promise<void> {
		// Si déjà chargé, on retourne immédiatement
		if (this.scriptLoaded) return;

		// Si un chargement est en cours, on attend qu'il se termine
		if (this.loadingPromise) return this.loadingPromise;

		this.loadingPromise = new Promise((resolve, reject) => {
			// En cas d'échec : on retire le script fautif et on réinitialise l'état
			// pour qu'un prochain appel puisse retenter (ex : coupure réseau passagère).
			const fail = (script: HTMLScriptElement, message: string) => {
				script.remove();
				this.loadingPromise = null;
				reject(new Error(message));
			};

			// Déjà présent dans le DOM (ex : navigation SPA), rien à refaire
			if (document.getElementById(this.CONFIG_ID)) {
				this.scriptLoaded = true;
				resolve();
				return;
			}

			const appendConfigScript = () => {
				if (document.getElementById(this.CONFIG_ID)) {
					this.scriptLoaded = true;
					resolve();
					return;
				}

				const config = document.createElement('script');
				config.id = this.CONFIG_ID;
				config.src = this.CONFIG_SRC;
				config.onload = () => {
					this.scriptLoaded = true;
					resolve();
				};
				config.onerror = () => fail(config, 'Failed to load Botpress config script');
				document.body.appendChild(config);
			};

			// inject.js déjà chargé : on ajoute directement la configuration
			if (document.getElementById(this.INJECT_ID)) {
				appendConfigScript();
				return;
			}

			const inject = document.createElement('script');
			inject.id = this.INJECT_ID;
			inject.src = this.INJECT_SRC;
			inject.onload = () => appendConfigScript();
			inject.onerror = () => fail(inject, 'Failed to load Botpress inject script');
			document.head.appendChild(inject);
		});

		return this.loadingPromise;
	}

}

export default function useBotpressService () {
	return BotpressService.getInstance();
}
